import { FC, useCallback, useEffect, useState } from 'react'
import { useRecordContext, useTranslate, useNotify } from 'react-admin'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SecurityIcon from '@mui/icons-material/Security'
import { getJson, getUrl, postJson } from '../../providers/data'
import { OrganizationOut } from '../../types/apiRequests'
import { border } from '../../layout/theme/border'
import { fadeSlideIn } from '../../components/dashboard'

interface IdpConnection {
  alias: string
  display_name: string
  provider_type: string
  enabled: boolean
}

const PROVIDER_TYPES = [
  { value: 'microsoft', label: 'Microsoft Entra ID' },
  { value: 'google', label: 'Google Workspace' },
  { value: 'saml', label: 'SAML 2.0' },
  { value: 'oidc', label: 'OpenID Connect' },
]

const AddIdpDialog: FC<{
  open: boolean
  onClose: () => void
  onSave: (data: Record<string, string>) => Promise<void>
}> = ({ open, onClose, onSave }) => {
  const translate = useTranslate()
  const [providerType, setProviderType] = useState('microsoft')
  const [alias, setAlias] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [issuerUrl, setIssuerUrl] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({
        alias,
        // eslint-disable-next-line camelcase
        provider_type: providerType,
        // eslint-disable-next-line camelcase
        display_name: displayName || alias,
        // eslint-disable-next-line camelcase
        client_id: clientId,
        // eslint-disable-next-line camelcase
        client_secret: clientSecret,
        // eslint-disable-next-line camelcase
        issuer_url: issuerUrl,
      })
      onClose()
    } catch {
      // Swallow the re-throw to avoid an unhandled rejection; dialog stays open with form state intact.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate('resources.organization.idp.addTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            label={translate('resources.organization.idp.providerType')}
            value={providerType}
            onChange={(e) => setProviderType(e.target.value)}
            fullWidth
          >
            {PROVIDER_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={translate('resources.organization.idp.alias')}
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="e.g. microsoft-contoso"
            fullWidth
            required
          />
          <TextField
            label={translate('resources.organization.idp.displayName')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Client Secret"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            type="password"
            fullWidth
            required
          />
          {(providerType === 'saml' || providerType === 'oidc') && (
            <TextField
              label={providerType === 'saml' ? 'SSO URL' : 'Issuer URL'}
              value={issuerUrl}
              onChange={(e) => setIssuerUrl(e.target.value)}
              fullWidth
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
        <Button onClick={() => { void handleSave() }} variant="contained" disabled={saving || !alias || !clientId}>
          {saving ? <CircularProgress size={20} /> : translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const OrganizationIdpTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  const notify = useNotify()
  const [idps, setIdps] = useState<IdpConnection[]>([])
  const [loading, setLoading] = useState(true)
  // Distinguish "empty list" from "failed to load" so a backend outage doesn't look like an empty tab and lead to duplicate IdPs.
  const [loadError, setLoadError] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const fetchIdps = useCallback(async () => {
    if (!record?.id) return
    setLoading(true)
    setLoadError(false)
    try {
      const data = await getJson<IdpConnection[]>(
        `${getUrl()}/organization/${record.id}/identity-providers`,
      )
      setIdps(data)
    } catch {
      setIdps([])
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [record?.id])

  useEffect(() => { void fetchIdps() }, [fetchIdps])

  const handleAdd = async (data: Record<string, string>) => {
    if (!record?.id) return
    try {
      await postJson(`${getUrl()}/organization/${record.id}/identity-providers`, data)
      notify('resources.organization.idp.added', { type: 'success' })
      await fetchIdps()
    } catch (err) {
      // Re-throw to keep Save enabled and form state intact; notify is the only error signal since postJson gives no body.
      notify(
        (err as Error)?.message || translate('resources.organization.idp.addFailed'),
        { type: 'error' },
      )
      throw err
    }
  }

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>

  return (
    <Box sx={{ p: 2, animation: `${fadeSlideIn} 0.3s ease-out` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {translate('resources.organization.idp.title')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => setDialogOpen(true)}
          disabled={!record?.idp_org_id}
        >
          {translate('resources.organization.idp.add')}
        </Button>
      </Box>

      {!record?.idp_org_id && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate('resources.organization.idp.notProvisioned')}
        </Typography>
      )}

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { void fetchIdps() }}>
              {translate('ra.action.refresh')}
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {translate('resources.organization.idp.loadFailed')}
        </Alert>
      )}

      {idps.length === 0 && !loadError && record?.idp_org_id && (
        <Typography color="text.secondary">
          {translate('resources.organization.idp.empty')}
        </Typography>
      )}

      {idps.map((idp) => (
        <Box
          key={idp.alias}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            mb: 1,
            border: border.thin,
            borderRadius: border.radius.md,
          }}
        >
          <SecurityIcon color="action" />
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600}>{idp.display_name || idp.alias}</Typography>
            <Typography variant="body2" color="text.secondary">{idp.alias}</Typography>
          </Box>
          <Chip
            label={PROVIDER_TYPES.find((t) => t.value === idp.provider_type)?.label || idp.provider_type}
            size="small"
          />
          <Chip
            label={idp.enabled ? 'Active' : 'Disabled'}
            color={idp.enabled ? 'success' : 'default'}
            size="small"
          />
        </Box>
      ))}

      <AddIdpDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleAdd} />
    </Box>
  )
}

export default OrganizationIdpTab
